import * as React from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import { useSearchParams } from "react-router-dom";

import {
  spacing,
  typography,
  colors,
} from "../../../../../../../../assets/styles/tokens";
import NegativeBalance from "./NegativeBalance/NegativeBalance";
import FraudPlayers from "./FraudPlayers/FraudPlayers";
import ConversionsUpload from "./ConversionsUpload/ConversionsUpload";

const tabConfig = [
  { label: "Fraud Players", value: "FraudPlayers", trackerId: "643" },
  { label: "Negative Balance", value: "NegativeBalance", trackerId: "647" },
  { label: "Conversions Upload", value: "ConversionsUpload", trackerId: "647" },
];

const TAB_COMPONENTS = [FraudPlayers, NegativeBalance,  ConversionsUpload];

interface TabPanelProps {
  children?: React.ReactNode;
  activeIndex: number;
  index: number;
  visited: boolean;
}

// Only renders children if tab has been visited at least once
// Once visited, keeps mounted with display:none (no re-mount, no re-fetch)
const TabPanel = React.memo(
  ({ children, activeIndex, index, visited }: TabPanelProps) => {
    if (!visited) return null; // never visited → don't mount → no API call
    return (
      <Box sx={{ display: activeIndex === index ? "block" : "none", pt: 1 }}>
        {children}
      </Box>
    );
  },
);

export default function BeAwareTabs() {
  const [searchParams, setSearchParams] = useSearchParams();

  const currentTabParam = searchParams.get("tabName");
  const initialIndex = tabConfig.findIndex(
    (tab) => tab.value === currentTabParam,
  );
  const activeIndex = initialIndex !== -1 ? initialIndex : 0;

  const [value, setValue] = React.useState(activeIndex);

  // Track which tabs have been visited — only visited tabs are mounted
  // On refresh: only the active tab index is pre-visited
  const workspaceId = searchParams.get("workspace");
  const [visitedTabs, setVisitedTabs] = React.useState<Set<number>>(
    () => new Set([activeIndex]),
  );

  // Reset visited tabs when workspace changes
  const prevWorkspaceRef = React.useRef(workspaceId);
  if (prevWorkspaceRef.current !== workspaceId) {
    prevWorkspaceRef.current = workspaceId;
    setVisitedTabs(new Set([value])); // ← runs during render, before effects
  }

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    // Mark this tab as visited so it mounts for the first time
    setVisitedTabs((prev) => new Set(prev).add(newValue));
    const newTabValue = tabConfig[newValue].value;
    searchParams.set("tabName", newTabValue);
    setSearchParams(searchParams);
  };

  return (
    <Box sx={{ height: "93vh", overflowY: "auto" }}>
      <Tabs
        value={value}
        onChange={handleChange}
        centered
        sx={{
          minHeight: spacing.Spacing["3xl"].$value,
          px: spacing.Radius["2xl"].$value,
          pb: spacing.Radius.md.$value,
          boxShadow: "0px 2px 5px -3px" + colors.GrayDarkMode[400].$value,
        }}
        TabIndicatorProps={{
          sx: {
            height: spacing.Spacing.none.$value,
          },
        }}
        slotProps={{
          list: {
            sx: {
              justifyContent: "flex-start",
            },
          },
        }}
      >
        {tabConfig.map((tab, index) => (
          <Tab
            key={tab.label}
            // disabled={loadingTrendsMobData}
            label={tab.label}
            disableRipple
            {...(tab.trackerId && value === index
              ? { "data-tracker": tab.trackerId }
              : {})}
            sx={{
              color: colors.GrayDarkMode[600].$value,
              textTransform: "none",
              fontWeight: typography.Font_weight.Medium.$value,
              borderRadius: spacing.Radius.md.$value,
              fontSize: typography.Size.Text.xs.$value,
              lineHeight: typography.Line_height.text.xs.$value,
              py: spacing.Radius.md.$value,
              px: spacing.Radius.xl.$value,
              minHeight: spacing.Spacing["3xl"].$value,
              letterSpacing: "0",
              "&.Mui-selected": {
                color: colors.Brand[600].$value,
                fontWeight: typography.Font_weight.Medium.$value,
                backgroundColor: colors.Brand[50].$value + "!important",
              },
              "&:hover": { backgroundColor: "transparent" },
            }}
          />
        ))}
      </Tabs>

      {TAB_COMPONENTS.map((Component, index) => (
        <TabPanel
          key={tabConfig[index].value}
          activeIndex={value}
          index={index}
          visited={visitedTabs.has(index)}
        >
          <Component />
        </TabPanel>
      ))}
    </Box>
  );
}
